/**
 * Building blocks shared by the integration settings pages (registry connections, TLD channels,
 * VNNIC providers): environment badges and banners, translated status badges, write-only secret
 * inputs, reason / confirmation fields, the dialog shell, write outcomes and error messages.
 * Every user-visible string comes from the `integrations` namespace.
 */

import { Building2, Check, FileKey2, FlaskConical, Globe, KeyRound, Network, ShieldAlert, Wand2, X, type LucideIcon } from 'lucide-react';
import { createContext, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import i18n from '@/i18n';
import { cn } from '@/lib/utils';
import { api, isApiError } from '../../api';
import type {
  AppliedConfig,
  ApprovalLink,
  ConnectionKind,
  ConnectionProtocol,
  ConnectionRef,
  FieldChange,
  IntegrationContext,
  IntegrationEnvironment,
  IntegrationProvider,
  SecretInput,
  SecretStatus,
  StaffRef,
  TldChannel,
  Warning,
} from '../../api/areas/integrations';
import { Badge, TONE_CLASS, type Tone } from '../../components/Badge';
import { Callout, Field } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { errorHint } from '../../components/Write';
import { formatCount, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';

type LooseT = (key: string, options?: Record<string, unknown>) => string;

/** Title-cases a code the translations do not know: `PENDING_REVIEW` -> `Pending review`. */
export function humanize(value: string): string {
  const s = value.replace(/[_.]+/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Translation for a key built at run time; falls back when the key does not exist. */
export function tx(key: string, fallback: string, options?: Record<string, unknown>): string {
  const full = `integrations:${key}`;
  return i18n.exists(full) ? (i18n.t as unknown as LooseT)(full, options) : fallback;
}

// ---------------------------------------------------------------------------
// Context (what this deployment runs), loaded once per page

const IntegrationCtx = createContext<IntegrationContext | null>(null);

/** Loads what this deployment runs (once per page) and renders the children with it. */
export function WithIntegrationContext({ children }: { children: ReactNode }) {
  const { t } = useTranslation('integrations');
  const result = useApi((signal) => api.getIntegrationContext({ signal }), []);
  if (result.status === 'loading') return <Loading label={t('common.loading')} rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('common.contextFailed')} />;
  return <IntegrationCtx.Provider value={result.data}>{children}</IntegrationCtx.Provider>;
}

/** Like [`WithIntegrationContext`], for a block embedded in another page: renders nothing while
 * loading or when the context cannot be read (e.g. no `integration.read`). */
export function WithIntegrationContextQuiet({ children }: { children: ReactNode }) {
  const result = useApi((signal) => api.getIntegrationContext({ signal }), []);
  if (result.status !== 'success') return null;
  return <IntegrationCtx.Provider value={result.data}>{children}</IntegrationCtx.Provider>;
}

/** The integration context of the current page; pages render children only after it loaded. */
export function useIntegrationContext(): IntegrationContext {
  const ctx = useContext(IntegrationCtx);
  if (!ctx) throw new Error('IntegrationContextProvider is missing');
  return ctx;
}

// ---------------------------------------------------------------------------
// The deployment (one environment per deployment, ADR-018): every setting on these pages is
// this server's only configuration; OT&E or production only labels the server.

const ENV_STYLE: Record<IntegrationEnvironment, { tone: Tone; icon: LucideIcon }> = {
  OTE: { tone: 'info', icon: FlaskConical },
  PRODUCTION: { tone: 'crit', icon: ShieldAlert },
};

const BADGE_BASE = 'inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium [&>svg]:size-3';

/** Server badge: production is always red with a shield, the OT&E (test) server blue. Old
 * history entries may carry a label no longer in use (e.g. SANDBOX); it is shown as is. */
export function EnvBadge({ env, long = false, className }: { env: IntegrationEnvironment; long?: boolean; className?: string }) {
  const s = ENV_STYLE[env] ?? { tone: 'neutral' as Tone, icon: FlaskConical };
  const Icon = s.icon;
  const label = tx(`env.long.${env}`, env);
  return (
    <span className={cn(BADGE_BASE, TONE_CLASS[s.tone], env === 'PRODUCTION' && 'font-semibold', className)} title={label}>
      <Icon aria-hidden="true" />
      {long ? label : tx(`env.short.${env}`, env)}
    </span>
  );
}

/** Large strip at the top of every dialog and panel that changes settings: which server this is
 * (its only configuration) and whether a change applies at once or needs a second person. */
export function EnvironmentBanner({ env, approvalRequired, message, className }: { env: IntegrationEnvironment; approvalRequired?: boolean; message?: ReactNode; className?: string }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const production = env === 'PRODUCTION';
  const needsApproval = approvalRequired ?? ctx.four_eyes;
  const Icon = ENV_STYLE[env].icon;
  return (
    <div
      role="note"
      className={cn(
        'flex gap-3 rounded-lg border-2 p-3 text-sm',
        production ? 'border-critical/60 bg-critical-soft' : 'border-info/40 bg-info-soft',
        className,
      )}
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', production ? 'text-critical' : 'text-info')} aria-hidden="true" />
      <div className="min-w-0 space-y-1">
        <div className={cn('font-semibold', production && 'text-critical')}>{t('env.editing', { env: t(`env.long.${env}`) })}</div>
        <div className="text-foreground/85">{message ?? (needsApproval ? t('env.approvalRequired') : t('env.immediate', { seconds: ctx.refresh_seconds }))}</div>
        <div className="text-xs text-muted-foreground">{t('env.oneConfiguration')}</div>
      </div>
    </div>
  );
}

/** One-line note about which server this is (OT&E or production), and the break-glass mode. */
export function DeploymentNote({ providers = false }: { providers?: boolean }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  return (
    <div className="space-y-2">
      <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>{t('env.deployment')}</span>
        <EnvBadge env={ctx.integration_environment} long />
        <span>{t('env.refresh', { seconds: ctx.refresh_seconds })}</span>
      </p>
      {providers && ctx.settings_source === 'env' ? (
        <Callout tone="warn" title={t('env.breakGlassTitle')}>
          {t('env.breakGlass')}
        </Callout>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connections, channels, statuses

const CHANNEL_STYLE: Record<TldChannel, { tone: Tone; icon: LucideIcon }> = {
  DIRECT_EPP: { tone: 'ok', icon: Network },
  REGISTRY_REST: { tone: 'info', icon: Globe },
  UPSTREAM_REGISTRAR: { tone: 'pending', icon: Building2 },
};

export function channelOf(kind: ConnectionKind, protocol: ConnectionProtocol): TldChannel {
  if (kind === 'UPSTREAM_REGISTRAR') return 'UPSTREAM_REGISTRAR';
  return protocol === 'EPP' ? 'DIRECT_EPP' : 'REGISTRY_REST';
}

/** Channel badge. `short` is for dense tables and cards; the full label stays in the tooltip. */
export function ChannelBadge({ channel, short = false, className }: { channel: TldChannel; short?: boolean; className?: string }) {
  const { t } = useTranslation('integrations');
  const s = CHANNEL_STYLE[channel];
  const Icon = s.icon;
  return (
    <span data-badge="channel" className={cn(BADGE_BASE, TONE_CLASS[s.tone], !short && 'whitespace-normal', className)} title={`${t(`channel.${channel}`)}. ${t(`channelHint.${channel}`)}`}>
      <Icon aria-hidden="true" />
      {short ? t(`channelShort.${channel}`) : t(`channel.${channel}`)}
    </span>
  );
}

const STATUS_TONE: Record<string, Record<string, Tone>> = {
  connectionStatus: { ACTIVE: 'ok', SUSPENDED: 'warn', RETIRED: 'neutral' },
  versionStatus: { PENDING_APPROVAL: 'pending', ACTIVE: 'ok', SUPERSEDED: 'neutral', REJECTED: 'crit', CANCELLED: 'neutral' },
  credentialStatus: { PENDING: 'pending', ACTIVE: 'ok', ROTATED: 'neutral', REVOKED: 'crit' },
  approvalStatus: { PENDING: 'pending', APPROVED: 'info', REJECTED: 'crit', EXPIRED: 'warn', EXECUTED: 'ok', CANCELLED: 'neutral' },
};

export type StatusGroup = 'connectionStatus' | 'versionStatus' | 'credentialStatus' | 'approvalStatus';

export function StatusBadge({ group, value, icon }: { group: StatusGroup; value: string; icon?: boolean }) {
  return (
    <Badge tone={STATUS_TONE[group]?.[value] ?? 'neutral'} title={value} icon={icon}>
      {tx(`${group}.${value}`, humanize(value))}
    </Badge>
  );
}

/** A connection shown by name, linking to its settings page. */
export function ConnectionLink({ connection, sub }: { connection: Pick<ConnectionRef, 'id' | 'code' | 'name'>; sub?: ReactNode }) {
  return <EntityRef label={connection.name} sub={sub ?? connection.code} id={connection.id} to={`/settings/registries/${encodeURIComponent(connection.code)}`} />;
}

export function StaffName({ staff, className }: { staff: StaffRef | null | undefined; className?: string }) {
  const { t } = useTranslation('integrations');
  if (!staff) return <span className={cn('text-muted-foreground', className)}>{t('common.outsideControlCenter')}</span>;
  return <EntityRef label={staff.display_name} id={staff.id} className={className} />;
}

/** Relative time with the absolute UTC time in the tooltip. */
export function When({ at, className }: { at: string | null | undefined; className?: string }) {
  if (!at) return <span className="text-muted-foreground">—</span>;
  return (
    <time dateTime={at} className={cn('whitespace-nowrap', className)} title={formatDateTime(at)}>
      {formatRelative(at)}
    </time>
  );
}

export function ProviderName({ provider }: { provider: IntegrationProvider }) {
  const { t } = useTranslation('integrations');
  return <>{t(`provider.name.${provider}`)}</>;
}

export function YesNo({ value }: { value: boolean }) {
  const { t } = useTranslation('integrations');
  return value ? (
    <span className="inline-flex items-center gap-1 text-success">
      <Check className="size-3.5" aria-hidden="true" /> {t('common.yes')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <X className="size-3.5" aria-hidden="true" /> {t('common.no')}
    </span>
  );
}

export function OnOffBadge({ on }: { on: boolean }) {
  const { t } = useTranslation('integrations');
  return <Badge tone={on ? 'ok' : 'neutral'}>{on ? t('common.on') : t('common.off')}</Badge>;
}

// ---------------------------------------------------------------------------
// Secrets (status only; values are write-only)

export function SecretStatusLine({ status, label }: { status: SecretStatus; label?: ReactNode }) {
  const { t } = useTranslation('integrations');
  const Icon = status.source === 'REFERENCE' ? KeyRound : FileKey2;
  return (
    <div className="grid gap-0.5 text-sm">
      {label ? <span className="text-xs font-medium text-muted-foreground">{label}</span> : null}
      {!status.configured ? (
        <Badge tone="warn">{t('secret.notConfigured')}</Badge>
      ) : status.destroyed ? (
        <Badge tone="neutral">{t('secret.destroyed')}</Badge>
      ) : (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <Badge tone="ok" title={t('secret.writeOnly')}>
            {t('secret.configured')}
          </Badge>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Icon className="size-3.5" aria-hidden="true" />
            {status.source ? t(`secret.source.${status.source}`) : null}
          </span>
        </span>
      )}
      {status.configured && status.reference ? (
        <span className="text-xs text-muted-foreground">
          {t('secret.reference')}: <code className="break-all rounded bg-muted px-1">{status.reference}</code>
        </span>
      ) : null}
      {status.changed_at ? (
        <span className="text-xs text-muted-foreground">
          {status.changed_by ? t('secret.changedBy', { when: formatRelative(status.changed_at), who: status.changed_by.display_name }) : t('secret.changed', { when: formatRelative(status.changed_at) })}
        </span>
      ) : null}
    </div>
  );
}

export type SecretMode = SecretInput['source'];

/** Local form state for one write-only secret field. Never pre-filled; dropped when the dialog closes. */
export interface SecretDraft {
  mode: SecretMode;
  value: string;
  reference: string;
}

export const emptySecret = (canKeep: boolean): SecretDraft => ({ mode: canKeep ? 'KEEP' : 'VALUE', value: '', reference: '' });

export function secretInput(d: SecretDraft): SecretInput {
  if (d.mode === 'KEEP') return { source: 'KEEP' };
  if (d.mode === 'REFERENCE') return { source: 'REFERENCE', reference: d.reference.trim() };
  return { source: 'VALUE', value: d.value };
}

export const secretDraftValid = (d: SecretDraft) => d.mode === 'KEEP' || (d.mode === 'VALUE' ? d.value.length > 0 : d.reference.trim().length > 0);

/**
 * Write-only secret input: keep the current value, enter a new one (encrypted by the platform)
 * or point to a secret store. The value is never shown again after saving.
 */
export function SecretField({
  label,
  draft,
  onChange,
  canKeep,
  keepLabel,
  env,
  multiline = false,
  hint,
}: {
  label: ReactNode;
  draft: SecretDraft;
  onChange: (d: SecretDraft) => void;
  canKeep: boolean;
  keepLabel?: string;
  env: IntegrationEnvironment;
  /** Multi-line values (private keys) are read from a file instead of typed. */
  multiline?: boolean;
  hint?: ReactNode;
}) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const id = useId();
  const [fileName, setFileName] = useState<string | null>(null);
  const modes: SecretMode[] = canKeep ? ['KEEP', 'VALUE', 'REFERENCE'] : ['VALUE', 'REFERENCE'];
  const readFile = (file: File | undefined) => {
    if (!file) return;
    void file.text().then((text) => {
      setFileName(file.name);
      onChange({ ...draft, value: text });
    });
  };
  const trimmed = draft.reference.trim();
  const refWarning =
    draft.mode !== 'REFERENCE' || !trimmed
      ? null
      : trimmed.startsWith('env:') && env === 'PRODUCTION'
        ? t('secretInput.envForbidden')
        : trimmed.startsWith('vault:') && !ctx.secret_backends.vault
          ? t('secretInput.vaultMissing')
          : !/^(env|file|vault):\S+$/.test(trimmed)
            ? t('secretInput.referenceFormat')
            : null;
  return (
    <fieldset className="grid gap-2 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{label}</legend>
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={draft.mode}
        onValueChange={(v) => {
          if (!v) return;
          setFileName(null);
          onChange({ mode: v as SecretMode, value: '', reference: '' });
        }}
        className="flex-wrap"
        aria-label={typeof label === 'string' ? label : undefined}
      >
        {modes.map((m) => (
          <ToggleGroupItem key={m} value={m} className="text-xs">
            {m === 'KEEP' && keepLabel ? keepLabel : t(`secretInput.mode.${m}`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {draft.mode === 'KEEP' ? <p className="text-xs text-muted-foreground">{t('secretInput.keepHint')}</p> : null}
      {draft.mode === 'VALUE' ? (
        multiline ? (
          <div className="grid gap-1.5">
            <label htmlFor={`${id}-file`} className="text-xs font-medium text-muted-foreground">
              {t('secretInput.fileLabel')}
            </label>
            <Input id={`${id}-file`} type="file" accept=".key,.pem,.txt" onChange={(e) => readFile(e.target.files?.[0])} />
            {fileName ? <p className="text-xs text-success">{t('secretInput.fileRead', { name: fileName, count: draft.value.length })}</p> : null}
            <p className="text-xs text-muted-foreground">{t('secretInput.valueHint')}</p>
          </div>
        ) : (
          <div className="grid gap-1.5">
            <label htmlFor={`${id}-value`} className="text-xs font-medium text-muted-foreground">
              {t('secretInput.valueLabel')}
            </label>
            <Input
              id={`${id}-value`}
              type="password"
              autoComplete="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore=""
              value={draft.value}
              onChange={(e) => onChange({ ...draft, value: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              {t('secretInput.valueHint')} {draft.value ? t('secretInput.length', { count: draft.value.length }) : null}
            </p>
          </div>
        )
      ) : null}
      {draft.mode === 'REFERENCE' ? (
        <div className="grid gap-1.5">
          <label htmlFor={`${id}-ref`} className="text-xs font-medium text-muted-foreground">
            {t('secretInput.referenceLabel')}
          </label>
          <Input
            id={`${id}-ref`}
            autoComplete="off"
            spellCheck={false}
            className="font-mono text-xs"
            placeholder={env === 'PRODUCTION' ? 'vault:kv/…#field' : 'file:/run/secrets/…'}
            value={draft.reference}
            onChange={(e) => onChange({ ...draft, reference: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">{env === 'PRODUCTION' ? t('secretInput.referenceHintProduction') : t('secretInput.referenceHint')}</p>
          {refWarning ? <p className="text-xs text-warning">{refWarning}</p> : null}
        </div>
      ) : null}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Form parts

export function ReasonField({ value, onChange, optional = false }: { value: string; onChange: (v: string) => void; optional?: boolean }) {
  const { t } = useTranslation('integrations');
  return (
    <Field label={optional ? t('form.noteOptional') : t('form.reason')} hint={t('form.reasonHint')}>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} maxLength={1000} rows={2} required={!optional} />
    </Field>
  );
}

/** On/off switch with a label and explanation, e.g. "Switch this environment on". */
export function SwitchRow({ label, hint, checked, onChange, disabled }: { label: ReactNode; hint?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
      <label htmlFor={id} className="min-w-0 space-y-0.5">
        <span className="block font-medium">{label}</span>
        {hint}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

/** Explicit confirmation checkbox; the text depends on the environment being changed. */
export function ConfirmCheck({ checked, onChange, env, text }: { checked: boolean; onChange: (v: boolean) => void; env?: IntegrationEnvironment | null; text?: ReactNode }) {
  const { t } = useTranslation('integrations');
  const id = useId();
  return (
    <div className={cn('flex items-start gap-2 rounded-md border p-3 text-sm', env === 'PRODUCTION' ? 'border-critical/40 bg-critical-soft/60' : 'bg-muted/40')}>
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <label htmlFor={id} className="leading-snug">
        {text ?? (env === 'PRODUCTION' ? t('form.confirmProduction') : env ? t('form.confirmTest', { env: t(`env.inline.${env}`) }) : t('form.confirm'))}
      </label>
    </div>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  unit,
  optional,
  raw = false,
  className,
}: {
  label: ReactNode;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  unit?: string;
  optional?: boolean;
  /** Show the range without thousands separators (ports, IDs). */
  raw?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('integrations');
  const fmt = (n: number) => (raw ? String(n) : formatCount(n));
  const hint = [min !== undefined && max !== undefined ? t('form.range', { min: fmt(min), max: fmt(max) }) : null, unit, optional ? t('form.optional') : null].filter(Boolean).join(' · ');
  return (
    <Field label={label} hint={hint || undefined} className={className}>
      <Input
        type="number"
        inputMode="numeric"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  className,
  hint,
  disabled,
}: {
  label: ReactNode;
  value: T;
  onChange: (v: T) => void;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  className?: string;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <Field label={label} className={className} hint={hint}>
      <Select value={value} onValueChange={(v) => onChange(v as T)} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** Dialog shell for changes. Clicking outside never discards a half-filled form; the body is
 * mounted only while open, so typed secrets are dropped when it closes. */
export function ChangeDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[92vh] overflow-y-auto [&>*]:min-w-0 [&_[data-badge=channel]]:whitespace-normal', wide ? 'sm:max-w-3xl' : 'sm:max-w-xl')} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {open ? children : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Results, warnings and errors

export function WarningList({ warnings }: { warnings: readonly (Warning & { tld_id?: string | null })[] }) {
  const { t } = useTranslation('integrations');
  if (!warnings.length) return null;
  return (
    <Callout tone="warn" title={t('warnings.title')}>
      <ul className="list-disc space-y-1 pl-4">
        {warnings.map((w, i) => (
          <li key={`${w.code}-${w.field ?? ''}-${i}`}>
            {tx(`warnings.${w.code}`, humanize(w.code))}
            {w.field ? <span className="text-xs text-muted-foreground"> ({fieldLabel(w.field)})</span> : null}
          </li>
        ))}
      </ul>
    </Callout>
  );
}

/** What happened after a change: applied now, or saved and waiting for a second person. */
export function WriteOutcome({ result, approval, warnings, env, appliedText }: { result: 'APPLIED' | 'PENDING_APPROVAL'; approval: ApprovalLink | null; warnings: readonly Warning[]; env?: IntegrationEnvironment | null; appliedText?: ReactNode }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  return (
    <div className="space-y-3" role="status">
      {result === 'APPLIED' ? (
        <Callout tone="ok" title={t('outcome.appliedTitle')}>
          {appliedText ?? t('outcome.applied', { seconds: ctx.refresh_seconds })}
        </Callout>
      ) : (
        <Callout tone="pending" title={t('outcome.pendingTitle')}>
          <p>{t('outcome.pending')}</p>
          {approval ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('outcome.approvalMeta', { permission: t(`permission.${approval.action}`), hours: ctx.approval_ttl_hours })}
            </p>
          ) : null}
          {env ? <p className="mt-1 text-xs text-muted-foreground">{t('outcome.pendingEnv', { env: t(`env.long.${env}`) })}</p> : null}
        </Callout>
      )}
      <WarningList warnings={warnings} />
    </div>
  );
}

interface FieldErrorDetail {
  field: string;
  code: string;
}

function fieldErrorsOf(details: unknown): FieldErrorDetail[] {
  if (!details || typeof details !== 'object' || !('fields' in details) || !Array.isArray(details.fields)) return [];
  return details.fields.filter((f): f is FieldErrorDetail => !!f && typeof f === 'object' && typeof (f as FieldErrorDetail).field === 'string' && typeof (f as FieldErrorDetail).code === 'string');
}

function retryAfter(details: unknown): number | null {
  return details && typeof details === 'object' && 'retry_after_seconds' in details && typeof details.retry_after_seconds === 'number' ? details.retry_after_seconds : null;
}

/** Error of a change or test, in operator words; unknown codes show the server's message. */
/**
 * "Fill in the OT&E / production address": the provider's published addresses for both kinds of
 * server, each with a button that only fills the form. The one for this server comes first; the
 * other carries a reminder that it belongs on the other server (ADR-018: every server keeps its
 * own configuration).
 */
export function PresetFill<P>({
  env,
  presets,
  describe,
  matches,
  onUse,
  note,
}: {
  env: IntegrationEnvironment;
  presets: readonly { environment: IntegrationEnvironment; value: P }[];
  describe: (p: P) => string;
  matches: (p: P) => boolean;
  onUse: (p: P) => void;
  note?: (p: P) => ReactNode;
}) {
  const { t } = useTranslation('integrations');
  if (!presets.length) return null;
  const ordered = [...presets].sort((a, b) => Number(b.environment === env) - Number(a.environment === env));
  return (
    <div className="grid gap-2 rounded-lg border border-info/30 bg-info-soft p-3 text-sm">
      <span className="font-medium">{t('presetFill.title')}</span>
      {ordered.map(({ environment, value }) => {
        const inUse = matches(value);
        return (
          <div key={environment} className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 space-y-0.5">
              <span className="flex flex-wrap items-center gap-2">
                <EnvBadge env={environment} />
                <code className="break-all rounded bg-background px-1 text-xs">{describe(value)}</code>
                {inUse ? <span className="text-xs text-success">{t('envDialog.presetInUse')}</span> : null}
              </span>
              {environment !== env ? <span className="block text-xs text-muted-foreground">{t('presetFill.otherServer', { env: t(`env.short.${env}`) })}</span> : null}
              {note?.(value)}
            </span>
            {inUse ? null : (
              <Button type="button" size="sm" variant="outline" className="h-auto max-w-full py-1.5 text-left whitespace-normal" onClick={() => onUse(value)}>
                <Wand2 /> {t(`presetFill.use.${environment}`)}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function IntegrationError({ error }: { error: unknown }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  if (!isApiError(error)) {
    return <Callout tone="crit" title={t('errors.failed')}>{error instanceof Error ? error.message : null}</Callout>;
  }
  const fields = error.code === 'VALIDATION_FAILED' ? fieldErrorsOf(error.details) : [];
  const retry = retryAfter(error.details);
  const hint = errorHint(error.code);
  return (
    <div role="alert" className="space-y-1.5 rounded-md border border-critical/30 bg-critical-soft p-3 text-sm">
      <p className="font-medium">{tx(`errors.code.${error.code}`, error.message, { seconds: retry ?? '', deployment: t(`deployment.${ctx.deployment_environment}`) })}</p>
      {fields.length ? (
        <ul className="list-disc space-y-0.5 pl-5">
          {fields.map((f, i) => (
            <li key={`${f.field}-${i}`}>
              <span className="font-medium">{fieldLabel(f.field)}</span>: {tx(`errors.field.${f.code}`, humanize(f.code))}
            </li>
          ))}
        </ul>
      ) : null}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      <p className="text-xs text-muted-foreground">{t('errors.meta', { code: error.code, requestId: error.requestId })}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field names and change lists

const TRAFFIC_CLASS_PATH = /^epp\.classes\.([A-Z_]+)\.(\w+)$/;

/** Operator label for a field path used in errors, warnings and change lists. */
export function fieldLabel(path: string): string {
  const exact = tx(`fields.${path.replace(/\./g, '_')}`, '');
  if (exact) return exact;
  const cls = TRAFFIC_CLASS_PATH.exec(path);
  if (cls?.[1] && cls[2]) return `${tx(`trafficClass.${cls[1]}`, humanize(cls[1]))} · ${tx(`fields.class_${cls[2]}`, humanize(cls[2]))}`;
  if (path.startsWith('options.')) return tx(`provider.field.${path.slice(8)}`, humanize(path.slice(8)));
  if (path.startsWith('secrets.')) return tx(`provider.secret.${path.slice(8)}`, humanize(path.slice(8)));
  const last = path.split('.').pop() ?? path;
  return tx(`fields.${last}`, humanize(last));
}

// Change lists name connections by id (`connection`, `connection_id`); operators need names.
// One list per page view is enough: it is cached briefly and shared by every change list.
const CONNECTION_FIELDS = new Set(['connection', 'connection_id']);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let connectionNames: { at: number; promise: Promise<ReadonlyMap<string, string>> } | null = null;

function loadConnectionNames(): Promise<ReadonlyMap<string, string>> {
  if (!connectionNames || Date.now() - connectionNames.at > 30_000) {
    const promise = api
      .listConnections({})
      .then((list): ReadonlyMap<string, string> => new Map(list.items.map((c) => [c.id, c.name])))
      .catch((): ReadonlyMap<string, string> => new Map());
    connectionNames = { at: Date.now(), promise };
  }
  return connectionNames.promise;
}

/** Names of the connections, by id, once any change in `changes` refers to one by id. */
function useConnectionNames(changes: readonly FieldChange[]): ReadonlyMap<string, string> | null {
  const needed = changes.some((c) => c.kind === 'VALUE' && CONNECTION_FIELDS.has(c.field) && [c.before, c.after].some((v) => typeof v === 'string' && UUID_RE.test(v)));
  const [names, setNames] = useState<ReadonlyMap<string, string> | null>(null);
  useEffect(() => {
    if (!needed) return undefined;
    let live = true;
    void loadConnectionNames().then((m) => {
      if (live) setNames(m);
    });
    return () => {
      live = false;
    };
  }, [needed]);
  return names;
}

const ISO_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function formatValue(field: string, value: unknown, names: ReadonlyMap<string, string> | null): ReactNode {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  if (typeof value === 'boolean') return tx(value ? 'common.yes' : 'common.no', String(value));
  if (typeof value === 'number') return field.endsWith('_ms') ? `${formatCount(value)} ms` : field.endsWith('iana_id') || field.endsWith('port') ? String(value) : formatCount(value);
  if (typeof value === 'string') {
    if (CONNECTION_FIELDS.has(field) && UUID_RE.test(value)) return names?.get(value) ?? <span className="font-mono text-xs">{value.slice(0, 8)}…</span>;
    if (field === 'channel') return value.split(', ').map((v) => tx(`channel.${v}`, v)).join(', ');
    if (field === 'status') return tx(`connectionStatus.${value}`, tx(`credentialStatus.${value}`, value));
    if (field === 'auth_method' || field.endsWith('.auth_method')) return tx(`authMethod.${value}`, value);
    if (field === 'kind') return tx(`kind.${value}`, value);
    if (field === 'protocol') return tx(`protocol.${value}`, value);
    if (field === 'adapter') return tx(`adapter.${value}`, value);
    if (ISO_TIME_RE.test(value) && !Number.isNaN(Date.parse(value))) return formatDateTime(value);
    return <span className="break-all">{value}</span>;
  }
  return <code className="break-all text-xs">{JSON.stringify(value)}</code>;
}

/** Before → after for plain fields; secrets only say they were replaced (and where they are kept)
 * or removed. Connections are shown by name. */
export function FieldChanges({ changes, compact = false }: { changes: readonly FieldChange[]; compact?: boolean }) {
  const { t } = useTranslation('integrations');
  const names = useConnectionNames(changes);
  if (!changes.length) return compact ? null : <p className="text-sm text-muted-foreground">{t('changes.noFieldChanges')}</p>;
  return (
    <ul className={cn('grid gap-1', compact ? 'text-xs' : 'text-sm')}>
      {changes.map((c, i) => (
        <li key={`${c.field}-${i}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-medium">{fieldLabel(c.field)}:</span>
          {c.kind === 'SECRET' ? (
            <span className="text-muted-foreground">{c.change === 'REMOVED' || !c.source ? t('changes.secretRemoved') : t('changes.secretReplaced', { source: t(`secret.source.${c.source}`) })}</span>
          ) : c.hidden ? (
            <span className="text-muted-foreground">{t('changes.hiddenValue')}</span>
          ) : (
            <span className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="text-muted-foreground line-through decoration-muted-foreground/50">{formatValue(c.field, c.before, names)}</span>
              <span aria-hidden="true">→</span>
              <span>{formatValue(c.field, c.after, names)}</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Which service instances run which version

export function AppliedByList({ items, activeVersion }: { items: readonly AppliedConfig[]; activeVersion?: number | null }) {
  const { t } = useTranslation('integrations');
  if (!items.length) return <p className="text-sm text-muted-foreground">{t('applied.none')}</p>;
  return (
    <ul className="grid gap-2">
      {items.map((a) => (
        <li key={`${a.service}/${a.instance}/${a.subject ?? ''}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm">
          <span className="min-w-0">
            <ServiceName service={a.service} instance={a.instance} className="font-medium" />
          </span>
          {a.subject ? (
            <span title={t('applied.subjectHint', { subject: a.subject })}>
              <Badge tone="neutral">{a.former_subject ? t('applied.formerSubject') : t('applied.moduleSubject')}</Badge>
            </span>
          ) : null}
          {a.source === 'ENVIRONMENT' ? (
            <Badge tone="warn" className="whitespace-normal">{t('applied.environment')}</Badge>
          ) : a.source === 'DISABLED' ? (
            <Badge tone="neutral">{t('applied.disabled')}</Badge>
          ) : (
            <Badge tone={a.up_to_date ? 'ok' : 'warn'}>{t('applied.version', { version: a.version ?? '—' })}</Badge>
          )}
          {a.source === 'DATABASE' && !a.up_to_date ? (
            <span className="text-xs text-warning">{activeVersion !== undefined && activeVersion !== null ? t('applied.behind', { version: activeVersion }) : t('applied.behindShort')}</span>
          ) : null}
          {a.last_error ? <span className="text-xs text-critical">{tx(`applied.error.${a.last_error.code}`, a.last_error.message)}</span> : null}
          <span className="ml-auto text-xs text-muted-foreground">
            {t('applied.checked')} <When at={a.checked_at} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** A service of the platform in operator words; the technical name and instance stay in the tooltip. */
export function ServiceName({ service, instance, className }: { service: string; instance?: string; className?: string }) {
  return (
    <span className={className} title={instance ? `${service} · ${instance}` : service}>
      {serviceLabel(service)}
    </span>
  );
}

export function serviceLabel(service: string): string {
  return tx(`service.${service.replace(/-/g, '_')}`, service);
}

// ---------------------------------------------------------------------------
// The three channels, explained once per page

/** What each channel means, for the explainer on the connection and TLD pages. */
export function ChannelGuide({ className }: { className?: string }) {
  const { t } = useTranslation('integrations');
  const channels: TldChannel[] = ['DIRECT_EPP', 'REGISTRY_REST', 'UPSTREAM_REGISTRAR'];
  return (
    <div className={cn('grid gap-2', className)}>
      <p className="font-medium">{t('channelGuide.title')}</p>
      <ul className="grid gap-2 md:grid-cols-3">
        {channels.map((c) => (
          <li key={c} className="grid content-start gap-1 rounded-md border bg-background/60 p-2.5">
            <ChannelBadge channel={c} className="whitespace-normal" />
            <span className="text-xs text-foreground/80">{t(`channelHint.${c}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A titled card like `Section`, whose header wraps: on narrow screens the actions move below
 * the title instead of squeezing it into a thin column.
 */
export function Panel({
  title,
  description,
  actions,
  children,
  flush = false,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <Card className={cn('min-w-0 gap-4 py-5 shadow-xs', flush && 'pb-0', className)}>
      {title || actions ? (
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5">
          <div className="min-w-0 flex-1 basis-64 space-y-1.5">
            {title ? <h2 className="text-base leading-snug font-semibold">{title}</h2> : null}
            {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cn('min-w-0', flush ? 'px-0' : 'px-5')}>{children}</div>
    </Card>
  );
}

/** A labelled value block used inside cards. */
export function Stat({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0 space-y-0.5', className)}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-sm break-words">{children}</div>
    </div>
  );
}
